import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Play, Eye, Clock, MoreVertical, Link as LinkIcon, Trash2, Download, Loader2 } from 'lucide-react'
import type { Video } from '../../types'
import { videosApi } from '../../services/api'
import { formatDuration, formatNumber } from '../../hooks'
import clsx from 'clsx'

interface VideoCardProps {
  video: Video
  onCreateLink?: () => void
  onDelete?: () => void
}

export default function VideoCard({ video, onCreateLink, onDelete }: VideoCardProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const handleDownload = async () => {
    if (video.download_url) {
      window.open(video.download_url, '_blank')
      return
    }
    setDownloading(true)
    try {
      const { download_url, status } = await videosApi.getDownloadUrl(video.id)
      if (status === 'ready') {
        window.open(download_url, '_blank')
      } else {
        alert('Download is being prepared by Cloudflare. Please try again in a minute.')
      }
    } catch {
      alert('Failed to get download URL.')
    } finally {
      setDownloading(false)
    }
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
        setConfirming(false)
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [menuOpen])
  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    processing: 'bg-blue-500/20 text-blue-400',
    ready: 'bg-emerald-500/20 text-emerald-400',
    error: 'bg-red-500/20 text-red-400',
  }

  return (
    <div className="group card hover:border-slate-600 transition-colors">
      {/* Thumbnail */}
      <Link to={`/videos/${video.id}`} className="block relative aspect-video mb-4 rounded-lg overflow-hidden bg-slate-700">
        {video.thumbnail_url ? (
          <img
            src={video.thumbnail_url}
            alt={video.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Play className="w-12 h-12 text-slate-500" />
          </div>
        )}

        {/* Duration badge */}
        {video.duration && (
          <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/80 rounded text-xs text-white">
            {formatDuration(video.duration)}
          </div>
        )}

        {/* Status badge */}
        <div className={clsx(
          'absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium',
          statusColors[video.status]
        )}>
          {video.status}
        </div>

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
            <Play className="w-6 h-6 text-white" />
          </div>
        </div>
      </Link>

      {/* Content */}
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <Link to={`/videos/${video.id}`} className="flex-1">
            <h3 className="font-medium text-white line-clamp-2 hover:text-primary-400 transition-colors">
              {video.title}
            </h3>
          </Link>
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => { setMenuOpen(!menuOpen); setConfirming(false) }}
              className="p-1 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-8 z-50 w-48 bg-slate-800 border border-slate-700 rounded-lg shadow-xl py-1">
                {!confirming ? (
                  <button
                    onClick={() => { setConfirming(true); setDeleteError(null) }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-slate-700 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                ) : (
                  <div className="px-3 py-2 space-y-2">
                    <p className="text-sm text-slate-300">Are you sure?</p>
                    {deleteError && (
                      <p className="text-xs text-red-400">{deleteError}</p>
                    )}
                    <div className="flex gap-2">
                      <button
                        disabled={deleting}
                        onClick={async () => {
                          try {
                            setDeleting(true)
                            setDeleteError(null)
                            await onDelete?.()
                            setMenuOpen(false)
                            setConfirming(false)
                          } catch {
                            setDeleteError('Failed to delete video')
                          } finally {
                            setDeleting(false)
                          }
                        }}
                        className="flex-1 px-2 py-1 text-xs font-medium bg-red-600 hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded transition-colors"
                      >
                        {deleting ? 'Deleting...' : 'Confirm'}
                      </button>
                      <button
                        disabled={deleting}
                        onClick={() => setConfirming(false)}
                        className="flex-1 px-2 py-1 text-xs font-medium bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-slate-300 rounded transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 text-sm text-slate-400">
          <div className="flex items-center gap-1">
            <Eye className="w-4 h-4" />
            <span>{formatNumber(video.total_views)} views</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            <span>{formatDuration(video.total_watch_time)}</span>
          </div>
        </div>

        {/* Tags */}
        {video.tags && video.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {video.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 bg-slate-700 rounded text-xs text-slate-300"
              >
                {tag}
              </span>
            ))}
            {video.tags.length > 3 && (
              <span className="px-2 py-0.5 text-xs text-slate-400">
                +{video.tags.length - 3} more
              </span>
            )}
          </div>
        )}

        {/* Quick actions */}
        {video.status === 'ready' && (
          <div className="flex gap-2 mt-2">
            {onCreateLink && (
              <button
                onClick={onCreateLink}
                className="flex-1 flex items-center justify-center gap-2 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-slate-300 transition-colors"
              >
                <LinkIcon className="w-4 h-4" />
                Create Link
              </button>
            )}
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="flex items-center justify-center gap-2 py-2 px-3 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 rounded-lg text-sm text-slate-300 transition-colors"
              title="Download MP4"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
